import { isEqual } from "lodash";

import { IconsEnum } from "components/icon";
import { ratingToTextMap } from "constants/rating";
import { TICKET_PRIORITY_LABEL_MAP } from "constants/tickets/priorities";
import { ticketDetailedSourcesLabel } from "helpers/tickets";
import { DetailedSource, Rating, TicketPriority, TicketStatusFormattedValues } from "interfaces/entities/ticket";
import { IAgent } from "store/entities/agents";
import {
  IRuleTriggerAgentChanged,
  IRuleTriggerDetailedSourceType,
  IRuleTriggerLanguage,
} from "store/entities/rules/interfaces";
import { ITag } from "store/entities/tags";
import { ITeam } from "store/entities/teams";
import { plural } from "utils/string";

import { WorkflowActionType, WorkflowConditionType } from "../../interfaces";
import { TRIGGER_ANY_AGENT_OPTION_KEY, TRIGGER_UNASSIGNED_OPTION_KEY } from "../triggers/utils";

import { IRuleForm, actionType, conditionType } from "./types";
import { TIME_CONDITION_EVENTS } from "./utils";

export const actionSwitcherDisplays: {
  label: string;
  bold?: boolean;
  actionType?: WorkflowActionType;
  icon?: IconsEnum;
}[] = [
  { label: "Assign", bold: true, icon: IconsEnum.FilterAgentAssignment },
  { label: "Set assignment", actionType: WorkflowActionType.Assign },
  { label: "Send", bold: true, icon: IconsEnum.Send },
  { label: "Send feedback request", actionType: WorkflowActionType.FeedbackRequest },
  { label: "Send follow-up", actionType: WorkflowActionType.FollowUp },
  { label: "Send message", actionType: WorkflowActionType.Message },
  { label: "Send private note", actionType: WorkflowActionType.PrivateNote },
  { label: "Set", bold: true, icon: IconsEnum.FilterCheckMarkCircle },
  { label: "Set status", actionType: WorkflowActionType.Status },
  { label: "Set priority", actionType: WorkflowActionType.Priority },
  { label: "Tag", bold: true, icon: IconsEnum.Tag },
  { label: "Add tag", actionType: WorkflowActionType.AddTag },
  { label: "Remove tag", actionType: WorkflowActionType.RemoveTag },
  { label: "Spam", bold: true, icon: IconsEnum.Spam },
  { label: "Move to Spam", actionType: WorkflowActionType.Spam },
  { label: "Move to all recent tickets", actionType: WorkflowActionType.Unspam },
  { label: "Followers", bold: true, icon: IconsEnum.Followers },
  { label: "Add followers", actionType: WorkflowActionType.AddFollowers },
  { label: "Remove followers", actionType: WorkflowActionType.RemoveFollowers },
];

export const conditionSwitcherDisplays: {
  label: string;
  bold?: boolean;
  conditionType?: WorkflowConditionType;
  icon?: IconsEnum;
}[] = [
  { label: "Ticket", bold: true, icon: IconsEnum.ConditionTicket },
  { label: "Ticket created", conditionType: WorkflowConditionType.TicketCreated },
  { label: "Ticket changed", conditionType: WorkflowConditionType.TicketChanged },
  { label: "Source", conditionType: WorkflowConditionType.Source },
  { label: "Status", conditionType: WorkflowConditionType.Status },
  { label: "Status changed", conditionType: WorkflowConditionType.StatusChanged },
  { label: "Priority", conditionType: WorkflowConditionType.Priority },
  { label: "Priority changed", conditionType: WorkflowConditionType.PriorityChanged },
  { label: "Rating", conditionType: WorkflowConditionType.Rating },
  { label: "Subject", conditionType: WorkflowConditionType.Subject },
  { label: "New message", conditionType: WorkflowConditionType.NewRequesterMessage },
  { label: "New private note", conditionType: WorkflowConditionType.NewPrivateAgentMessage },
  { label: "New agent reply", conditionType: WorkflowConditionType.NewPublicAgentMessage },
  { label: "Tag", conditionType: WorkflowConditionType.Tag },
  { label: "Language", conditionType: WorkflowConditionType.Language },
  { label: "Event count for", conditionType: WorkflowConditionType.EventCount },
  { label: "Forwarding address", conditionType: WorkflowConditionType.Mailbox },
  { label: "Assignment", bold: true, icon: IconsEnum.FilterAgentAssignment },
  { label: "Team", conditionType: WorkflowConditionType.Team },
  { label: "Team changed", conditionType: WorkflowConditionType.TeamChanged },
  { label: "Agent", conditionType: WorkflowConditionType.Agent },
  { label: "Agent changed", conditionType: WorkflowConditionType.AgentChanged },
  { label: "Follower", conditionType: WorkflowConditionType.Follower },
  { label: "Requester", bold: true, icon: IconsEnum.FilterAgent },
  { label: "Requester's email", conditionType: WorkflowConditionType.Requester },
  { label: "Spam", bold: true, icon: IconsEnum.Spam },
  { label: "Ticket is spam", conditionType: WorkflowConditionType.IsSpam },
  { label: "Ticket is not spam", conditionType: WorkflowConditionType.IsNotSpam },
  { label: "Time", bold: true, icon: IconsEnum.TimeSolid },
  { label: "Time passed since", conditionType: WorkflowConditionType.Time },
];

export const timeUnitLabel = (timeUnit: number): string => {
  if (timeUnit === 60) {
    return "minutes";
  }

  if (timeUnit === 3600) {
    return "hours";
  }

  if (timeUnit === 86400) {
    return "days";
  }

  return "seconds";
};

export const guessTimeUnit = (value: number): number => {
  if (value % 86400 === 0) {
    return 86400;
  }

  if (value % 3600 === 0) {
    return 3600;
  }

  if (value % 60 === 0) {
    return 60;
  }

  return 1;
};

export const guessDescription = (
  rule: IRuleForm,
  agents: Record<string, IAgent>,
  teams: Record<string, ITeam>,
  tags: Record<string, ITag>,
  mailboxes: Record<string, { fullEmail: string }>,
  languageLabels: Record<string, string>
): string | null => {
  const firstAction = rule?.actions?.[0];
  const firstCondition = rule?.triggers?.[0];

  if (!firstAction || !firstCondition) {
    return null;
  }

  const currentDescription = rule.description;

  if (!firstAction.type || !firstCondition.type) {
    return currentDescription === "" ? null : "";
  }

  const eventTypeMap = {
    status: "Status change",
    assignment: "Assignment change",
    followUpSent: "Follow-up has been sent",
    priority: "Priority change",
    ticketCreation: "Ticket creation",
    message: "Message sent",
  };

  let guessedDescription = "";
  const firstConditionType = conditionType(firstCondition);
  const firstActionType = actionType(firstAction);

  switch (firstConditionType) {
    case WorkflowConditionType.TicketCreated:
      guessedDescription += "If ticket is created";
      break;
    case WorkflowConditionType.TicketChanged:
      guessedDescription += "If ticket has changed";
      break;
    case WorkflowConditionType.Source:
      guessedDescription += `If ticket source ${(firstCondition as IRuleTriggerDetailedSourceType).invert ? "is not" : "is"} ${
        ticketDetailedSourcesLabel[((firstCondition as IRuleTriggerDetailedSourceType).in as DetailedSource[])[0]]
      }`;
      break;
    case WorkflowConditionType.Status: {
      // @ts-expect-error TS(2339)
      if (firstCondition.in.length > 1) {
        // @ts-expect-error TS(2339)
        guessedDescription = `If the status ${firstCondition.invert ? "is not" : "is"} among ${firstCondition.in // FIXME: Fix this the next time the file is edited.
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .map((status: any) => TicketStatusFormattedValues[status])
          .join(", ")}`;
      } else {
        // @ts-expect-error TS(2339)
        guessedDescription = `If the status ${firstCondition.invert ? "is not" : "is"} ${firstCondition.in // FIXME: Fix this the next time the file is edited.
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .map((status: any) => TicketStatusFormattedValues[status])
          .join(", ")}`;
      }
      break;
    }
    case WorkflowConditionType.Priority: {
      // @ts-expect-error TS(2339)
      if (firstCondition.in.length > 1) {
        // @ts-expect-error TS(2339)
        guessedDescription = `If the priority ${firstCondition.invert ? "is not" : "is"} among ${firstCondition.in // FIXME: Fix this the next time the file is edited.
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .map((priority: any) => TICKET_PRIORITY_LABEL_MAP[priority])
          .join(", ")}`;
      } else {
        // @ts-expect-error TS(2339)
        guessedDescription = `If the priority ${firstCondition.invert ? "is not" : "is"} ${firstCondition.in // FIXME: Fix this the next time the file is edited.
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .map((priority: any) => TICKET_PRIORITY_LABEL_MAP[priority])
          .join(", ")}`;
      }

      break;
    }
    case WorkflowConditionType.PriorityChanged: {
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      const fromPriorities = (firstCondition.oldPriorityIn || []).map(
        // FIXME: Fix this the next time the file is edited.
        // @ts-expect-error TS(7053)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (priority: any) => TICKET_PRIORITY_LABEL_MAP[priority]
      );
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      const toPriorities = (firstCondition.newPriorityIn || []).map(
        // FIXME: Fix this the next time the file is edited.
        // @ts-expect-error TS(7053)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (priority: any) => TICKET_PRIORITY_LABEL_MAP[priority]
      );

      // @ts-expect-error TS(2339)
      const isFromAny = firstCondition.oldPriorityIn === undefined;
      // @ts-expect-error TS(2339)
      const isToAny = firstCondition.newPriorityIn === undefined;

      guessedDescription += "If the priority has changed from ";

      if (isFromAny) {
        guessedDescription += "any priority";
      } else if (fromPriorities.length > 1) {
        guessedDescription += `among ${fromPriorities.join(", ")}`;
      } else {
        guessedDescription += fromPriorities.join(", ");
      }

      guessedDescription += " to ";

      if (isToAny) {
        guessedDescription += "any priority";
      } else if (toPriorities.length > 1) {
        guessedDescription += `any of ${toPriorities.join(", ")}`;
      } else {
        guessedDescription += toPriorities.join(", ");
      }

      break;
    }
    case WorkflowConditionType.StatusChanged: {
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fromStatuses = (firstCondition.oldStatusIn || []).map((status: any) => TicketStatusFormattedValues[status]);
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const toStatuses = (firstCondition.newStatusIn || []).map((status: any) => TicketStatusFormattedValues[status]);

      // @ts-expect-error TS(2339)
      const isFromAny = firstCondition.oldStatusIn === undefined;
      // @ts-expect-error TS(2339)
      const isToAny = firstCondition.newStatusIn === undefined;

      guessedDescription += "If the status has changed from ";

      if (isFromAny) {
        guessedDescription += "any status";
      } else if (fromStatuses.length > 1) {
        guessedDescription += `among ${fromStatuses.join(", ")}`;
      } else {
        guessedDescription += fromStatuses.join(", ");
      }

      guessedDescription += " to ";

      if (isToAny) {
        guessedDescription += "any status";
      } else if (toStatuses.length > 1) {
        guessedDescription += `any of ${toStatuses.join(", ")}`;
      } else {
        guessedDescription += toStatuses.join(", ");
      }

      break;
    }
    case WorkflowConditionType.AgentChanged: {
      const agentChangedCondition = firstCondition as IRuleTriggerAgentChanged;
      const fromAgents = (agentChangedCondition.oldAgentIn || [])
        .map((agentID: string) => {
          if (agentID === TRIGGER_UNASSIGNED_OPTION_KEY) {
            return { name: "unassigned" };
          }

          if (agentID === TRIGGER_ANY_AGENT_OPTION_KEY) {
            return { name: "any agent" };
          }

          return agents[agentID];
        })
        .filter(Boolean);

      const toAgents = (agentChangedCondition.newAgentIn || [])
        .map((agentID: string) => {
          if (agentID === TRIGGER_UNASSIGNED_OPTION_KEY) {
            return { name: "unassigned" };
          }

          if (agentID === TRIGGER_ANY_AGENT_OPTION_KEY) {
            return { name: "any agent" };
          }

          return agents[agentID];
        })
        .filter(Boolean);

      const isFromAnyAgentAndUnassigned = agentChangedCondition.oldAgentIn === undefined;

      const isToAnyAgent = agentChangedCondition.newAgentIn === undefined;

      guessedDescription += "If the agent assignment has changed from ";

      if (isFromAnyAgentAndUnassigned) {
        guessedDescription += "any agent or unassigned";
      } else if (fromAgents.length > 1) {
        guessedDescription += `among ${fromAgents.map(agent => agent.name).join(", ")}`;
      } else {
        guessedDescription += fromAgents.map(agent => agent.name).join(", ");
      }

      guessedDescription += " to ";

      if (isToAnyAgent) {
        guessedDescription += "any agent";
      } else if (toAgents.length > 1) {
        guessedDescription += `any of ${toAgents.map(agent => agent.name).join(", ")}`;
      } else {
        guessedDescription += toAgents.map(agent => agent.name).join(", ");
      }

      break;
    }
    case WorkflowConditionType.TeamChanged: {
      // FIXME: Fix this the next time the file is edited.
      // @ts-expect-error TS(2339)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fromTeams = (firstCondition.oldTeamIn || []).map((teamID: any) => teams[teamID]).filter(Boolean);
      // FIXME: Fix this the next time the file is edited.
      // @ts-expect-error TS(2339)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const toTeams = (firstCondition.newTeamIn || []).map((teamID: any) => teams[teamID]).filter(Boolean);

      // @ts-expect-error TS(2339)
      const isFromAny = firstCondition.oldTeamIn === undefined;
      // @ts-expect-error TS(2339)
      const isToAny = firstCondition.newTeamIn === undefined;

      guessedDescription += "If the team assignment has changed from ";

      if (isFromAny) {
        guessedDescription += "any team";
      } else if (fromTeams.length > 1) {
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        guessedDescription += `among ${fromTeams.map((t: any) => t.name).join(", ")}`;
      } else {
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        guessedDescription += fromTeams.map((t: any) => t.name).join(", ");
      }

      guessedDescription += " to ";

      if (isToAny) {
        guessedDescription += "any team";
      } else if (toTeams.length > 1) {
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        guessedDescription += `any of ${toTeams.map((t: any) => t.name).join(", ")}`;
      } else {
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        guessedDescription += toTeams.map((t: any) => t.name).join(", ");
      }

      break;
    }
    case WorkflowConditionType.Subject: {
      const phrases =
        // @ts-expect-error TS(2339)
        firstCondition.containsIn ||
        // @ts-expect-error TS(2339)
        firstCondition.containsInCI ||
        // @ts-expect-error TS(2339)
        firstCondition.containsWholePhraseIn ||
        // @ts-expect-error TS(2339)
        firstCondition.containsWholePhraseInCI;

      // @ts-expect-error TS(2339)
      guessedDescription += `If the subject ${firstCondition.invert ? "does not contain" : "contains"} `;
      if (phrases.length > 1) {
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        guessedDescription += `any of ${phrases.map((phrase: any) => `“${phrase}”`).join(", ")}`;
      } else {
        guessedDescription += `“${phrases[0]}”`;
      }
      break;
    }
    case WorkflowConditionType.NewRequesterMessage: {
      const phrases =
        // @ts-expect-error TS(2339)
        firstCondition.containsIn ||
        // @ts-expect-error TS(2339)
        firstCondition.containsInCI ||
        // @ts-expect-error TS(2339)
        firstCondition.containsWholePhraseIn ||
        // @ts-expect-error TS(2339)
        firstCondition.containsWholePhraseInCI;

      // @ts-expect-error TS(2339)
      guessedDescription += `If a new message ${firstCondition.invert ? "does not contain" : "contains"} `;
      if (phrases.length > 1) {
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        guessedDescription += `any of ${phrases.map((phrase: any) => `“${phrase}”`).join(", ")}`;
      } else {
        guessedDescription += `“${phrases[0]}”`;
      }
      break;
    }
    case WorkflowConditionType.NewPrivateAgentMessage: {
      const phrases =
        // @ts-expect-error TS(2339)
        firstCondition.containsIn ||
        // @ts-expect-error TS(2339)
        firstCondition.containsInCI ||
        // @ts-expect-error TS(2339)
        firstCondition.containsWholePhraseIn ||
        // @ts-expect-error TS(2339)
        firstCondition.containsWholePhraseInCI;

      // @ts-expect-error TS(2339)
      guessedDescription += `If a new private note ${firstCondition.invert ? "does not contain" : "contains"} `;
      if (phrases.length > 1) {
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        guessedDescription += `any of ${phrases.map((phrase: any) => `“${phrase}”`).join(", ")}`;
      } else {
        guessedDescription += `“${phrases[0]}”`;
      }
      break;
    }
    case WorkflowConditionType.NewAnyAgentMessage: {
      const phrases =
        // @ts-expect-error TS(2339)
        firstCondition.containsIn ||
        // @ts-expect-error TS(2339)
        firstCondition.containsInCI ||
        // @ts-expect-error TS(2339)
        firstCondition.containsWholePhraseIn ||
        // @ts-expect-error TS(2339)
        firstCondition.containsWholePhraseInCI;

      // @ts-expect-error TS(2339)
      guessedDescription += `If a new agent reply ${firstCondition.invert ? "does not contain" : "contains"} `;
      if (phrases.length > 1) {
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        guessedDescription += `any of ${phrases.map((phrase: any) => `“${phrase}”`).join(", ")}`;
      } else {
        guessedDescription += `“${phrases[0]}”`;
      }
      break;
    }
    case WorkflowConditionType.Mailbox: {
      // @ts-expect-error TS(2339)
      guessedDescription += `If a ticket ${firstCondition.invert ? "is not" : "is"} forwarded to `;
      // @ts-expect-error TS(2339)
      const collection = firstCondition.in;
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const emails = collection.map((id: any) => mailboxes[id]?.fullEmail).filter(Boolean);

      if (collection.length > 1) {
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        guessedDescription += `any of ${emails.map((email: any) => `“${email}”`).join(", ")}`;
      } else {
        guessedDescription += `“${emails[0]}“`;
      }
      break;
    }
    case WorkflowConditionType.Tag: {
      const concreteType = firstCondition.type;
      // FIXME: Fix this the next time the file is edited.
      // @ts-expect-error TS(2339)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tagList = firstCondition.in.map((tID: any) => tags[tID]?.name).filter(Boolean);

      guessedDescription += "If a tag";

      if (concreteType === "tagAdded") {
        if (tagList.length > 1) {
          guessedDescription = `If any of tags named ${tagList.join(", ")} have been added`;
        } else {
          guessedDescription = `If a tag named ${tagList[0]} has been added`;
        }
      } else if (concreteType === "tagRemoved") {
        if (tagList.length > 1) {
          guessedDescription = `If any of tags named ${tagList.join(", ")} have been removed`;
        } else {
          guessedDescription = `If a tag named ${tagList[0]} has been removed`;
        }
      } else {
        // @ts-expect-error TS(2339)
        guessedDescription += firstCondition.invert ? " is not " : " is ";
        if (tagList.length > 1) {
          guessedDescription += `any of ${tagList.join(", ")}`;
        } else {
          guessedDescription += tagList[0];
        }
      }

      break;
    }
    case WorkflowConditionType.Follower: {
      const concreteType = firstCondition.type;
      // FIXME: Fix this the next time the file is edited.
      // @ts-expect-error TS(2339)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const followerList = firstCondition.in.map((aID: any) => agents[aID]?.name).filter(Boolean);

      guessedDescription += "If a follower";

      if (concreteType === "followerAdded") {
        if (followerList.length > 1) {
          guessedDescription = `If any of followers among ${followerList.join(", ")} have been added`;
        } else {
          guessedDescription = `If a follower ${followerList[0]} has been added`;
        }
      } else if (concreteType === "followerRemoved") {
        if (followerList.length > 1) {
          guessedDescription = `If any of followers among ${followerList.join(", ")} have been removed`;
        } else {
          guessedDescription = `If a follower ${followerList[0]} has been removed`;
        }
      } else {
        // @ts-expect-error TS(2339)
        guessedDescription += firstCondition.invert ? " is not " : " is ";
        if (followerList.length > 1) {
          guessedDescription += `any of ${followerList.join(", ")}`;
        } else {
          guessedDescription += followerList[0];
        }
      }

      break;
    }
    case WorkflowConditionType.EventCount: {
      guessedDescription += "If the event count is reached";
      break;
    }
    case WorkflowConditionType.Team: {
      // FIXME: Fix this the next time the file is edited.
      // @ts-expect-error TS(2339)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const teamList = firstCondition.in.map((teamID: any) => teams[teamID]?.name).filter(Boolean);

      if (teamList.length > 1) {
        // @ts-expect-error TS(2339)
        guessedDescription += `If the assigned team ${firstCondition.invert ? "is not" : "is"} among ${teamList.join(
          ", "
        )}`;
      } else {
        // @ts-expect-error TS(2339)
        guessedDescription += `If the assigned team ${firstCondition.invert ? "is not" : "is"} ${teamList.join(", ")}`;
      }
      break;
    }
    case WorkflowConditionType.Agent: {
      // FIXME: Fix this the next time the file is edited.
      // @ts-expect-error TS(2339)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const agentList = firstCondition.in?.map((agentID: any) => agents[agentID]?.name).filter(Boolean);

      if (agentList?.length > 1) {
        // @ts-expect-error TS(2339)
        guessedDescription += `If the assigned agent ${firstCondition.invert ? "is not" : "is"} among ${agentList.join(
          ", "
        )}`;
      } else if (agentList) {
        // @ts-expect-error TS(2339)
        guessedDescription += `If the assigned agent ${firstCondition.invert ? "is not" : "is"} ${agentList.join(
          ", "
        )}`;
      } else if (firstCondition?.type === "hasAssignee" && !firstCondition?.eq) {
        // @ts-expect-error TS(2339)
        guessedDescription += `If the assigned agent ${firstCondition.invert ? "is not" : "is"} none`;
      } else {
        // @ts-expect-error TS(2339)
        guessedDescription += `If the assigned agent ${firstCondition.invert ? "is not" : "is"} any agent`;
      }
      break;
    }
    case WorkflowConditionType.Requester: {
      // @ts-expect-error TS(2339)
      const isContains = firstCondition.containsIn !== undefined;
      // FIXME: Fix this the next time the file is edited.
      // @ts-expect-error TS(2339)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const phraseList = isContains ? firstCondition.containsIn.map((phrase: any) => `“${phrase}”`) : firstCondition.in;

      let label;

      if (phraseList.length > 1) {
        label = isContains ? "contains any of" : "is one of";
        // @ts-expect-error TS(2339)
        if (firstCondition.inverse) {
          label = isContains ? "does not contain any of" : "is not one of";
        }
      } else {
        label = isContains ? "contains" : "is";
        // @ts-expect-error TS(2339)
        if (firstCondition.inverse) {
          label = isContains ? "does not contain" : "is not";
        }
      }

      guessedDescription += "If the requester's email";

      if (phraseList.length > 1) {
        guessedDescription += ` ${label} ${phraseList.join(", ")}`;
      } else {
        guessedDescription += ` ${label} ${phraseList[0]}`;
      }
      break;
    }
    case WorkflowConditionType.Time: {
      // @ts-expect-error TS(2339)
      const timeUnit = guessTimeUnit(firstCondition.eq);
      const timeLabel = timeUnitLabel(timeUnit);
      // @ts-expect-error TS(2339)
      const timeValue = firstCondition.eq / timeUnit;
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      const firstEventType = eventTypeMap[firstCondition.eventTypeIn[0]] || firstCondition.eventTypeIn[0];
      // @ts-expect-error TS(2339)
      const eventTypeInLength = firstCondition.eventTypeIn.length;

      // @ts-expect-error TS(2339)
      const eventTypeLabel = isEqual(firstCondition.eventTypeIn, TIME_CONDITION_EVENTS)
        ? "any ticket activity"
        : plural(
            // @ts-expect-error TS(2339)
            firstCondition.eventTypeIn.length,
            firstEventType,
            `${firstEventType} or ${eventTypeInLength - 1} other event${eventTypeInLength > 2 ? "s" : ""}`
          );

      guessedDescription += `If ${timeValue} ${plural(
        timeValue,
        timeLabel.slice(0, -1),
        timeLabel
      )} passed since ${eventTypeLabel}`;

      break;
    }
    case WorkflowConditionType.IsSpam: {
      guessedDescription += "If a ticket is spam";
      break;
    }
    case WorkflowConditionType.IsNotSpam: {
      guessedDescription += "If a ticket is not spam";
      break;
    }
    case WorkflowConditionType.Rating: {
      // @ts-expect-error TS(2339)
      if (firstCondition.in && firstCondition.in.length > 0) {
        // @ts-expect-error TS(2339)
        guessedDescription += `If the rating is ${firstCondition.in
          .map((rating: Rating) => ratingToTextMap[rating])
          .join(" or ")}`;
      }
      // @ts-expect-error TS(2339)
      if (firstCondition.newRatingIn && firstCondition.newRatingIn.length > 0) {
        // FIXME: Fix this the next time the file is edited.
        // @ts-expect-error TS(2339)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        guessedDescription += `If a${firstCondition.newRatingIn[0] === Rating.Neutral ? "n" : ""} ${firstCondition.newRatingIn.map((rating: any) => ratingToTextMap[rating]).join(" or ")} rating has been added`;
      }
      break;
    }
    case WorkflowConditionType.Language: {
      const trigger = firstCondition as IRuleTriggerLanguage;
      const languageList = trigger.in;
      const labels = languageList.map(key => (key in languageLabels ? languageLabels[key] : "Not detected"));
      const orMore = labels.length > 3 ? ` or ${labels.length - 3} other` : "";

      if (trigger.in.length === 1) {
        if (trigger.in[0] === "none") {
          guessedDescription += trigger.invert ? "If some language was detected" : "If no language was detected";
        } else {
          guessedDescription += `If the detected language ${trigger.invert ? "is not" : "is"} ${labels[0]}`;
        }
      } else {
        guessedDescription += `If the detected language ${trigger.invert ? "is not" : "is"} either ${labels.slice(0, 3).join(" or ")}${orMore}`;
      }

      break;
    }
    default:
      return null;
  }

  switch (firstActionType) {
    case WorkflowActionType.FeedbackRequest:
      guessedDescription += ", send a feedback request.";
      break;
    case WorkflowActionType.FollowUp:
      guessedDescription += ", send a follow-up.";
      break;
    case WorkflowActionType.Message:
      guessedDescription += ", send a message.";
      break;
    case WorkflowActionType.PrivateNote:
      guessedDescription += ", send a private note.";
      break;
    case WorkflowActionType.Status:
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      guessedDescription += `, set status to ${TicketStatusFormattedValues[firstAction.status]}.`;
      break;
    case WorkflowActionType.Priority: {
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      const priorityLabel = TICKET_PRIORITY_LABEL_MAP[firstAction.priority];
      guessedDescription += `, set priority to ${priorityLabel || "Unknown"}.`;
      break;
    }
    case WorkflowActionType.Spam:
      guessedDescription += ", mark ticket as spam.";
      break;
    case WorkflowActionType.Unspam:
      guessedDescription += ", mark ticket as not spam.";
      break;
    case WorkflowActionType.Assign:
      guessedDescription += ", set assignment.";
      break;
    case WorkflowActionType.AddTag: {
      // FIXME: Fix this the next time the file is edited.
      // @ts-expect-error TS(2339)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tagList = firstAction.tagIDs.map((tagID: any) => tags[tagID]?.name).filter(Boolean);
      if (tagList.length > 1) {
        guessedDescription += `, add following tags: ${tagList.join(", ")}.`;
      } else {
        guessedDescription += `, add a tag named ${tagList[0]}.`;
      }
      break;
    }
    case WorkflowActionType.RemoveTag: {
      // FIXME: Fix this the next time the file is edited.
      // @ts-expect-error TS(2339)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tagList = firstAction.tagIDs.map((tagID: any) => tags[tagID]?.name).filter(Boolean);
      if (tagList.length > 1) {
        guessedDescription += `, remove following tags: ${tagList.join(", ")}.`;
      } else {
        guessedDescription += `, remove a tag named ${tagList[0]}.`;
      }
      break;
    }
    case WorkflowActionType.AddFollowers: {
      // FIXME: Fix this the next time the file is edited.
      // @ts-expect-error TS(2339)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const followersList = firstAction.followers.map((agentID: any) => agents[agentID]?.name).filter(Boolean);

      guessedDescription += `, make ${followersList.join(", ")} follow this ticket.`;
      break;
    }
    case WorkflowActionType.RemoveFollowers: {
      // FIXME: Fix this the next time the file is edited.
      // @ts-expect-error TS(2339)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const followersList = firstAction.followers.map((agentID: any) => agents[agentID]?.name).filter(Boolean);

      guessedDescription += `, remove ${followersList.join(", ")} from followers list.`;
      break;
    }
    default:
      return null;
  }

  if (guessedDescription === currentDescription) {
    return null;
  }

  return guessedDescription;
};

export const guessRuleName = (
  rule: IRuleForm,
  agents: Record<string, IAgent>,
  teams: Record<string, ITeam>
): string | null => {
  const firstAction = rule.actions[0];
  if (!firstAction) {
    return null;
  }

  const currentName = rule.name;
  const type = actionType(firstAction);

  if (!type) {
    return currentName === "" ? null : "";
  }

  let guessedName = null;
  const display = actionSwitcherDisplays.find(listItem => listItem.actionType === type);

  if (!display) {
    return currentName === "" ? null : "";
  }

  guessedName = display.label;
  if (type === WorkflowActionType.Status) {
    // @ts-expect-error TS(2339)
    if (firstAction.status) {
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      guessedName += ` to ${TicketStatusFormattedValues[firstAction.status]}`;
    } else {
      return null;
    }
  }

  if (type === WorkflowActionType.Priority) {
    const priorityLabel = TICKET_PRIORITY_LABEL_MAP[(firstAction as { priority: TicketPriority }).priority];
    if (priorityLabel !== undefined) {
      guessedName += ` to ${priorityLabel}`;
    } else {
      return null;
    }
  }

  // @ts-expect-error TS(2339)
  if (type === WorkflowActionType.Assign && firstAction?.teamID !== undefined) {
    // @ts-expect-error TS(2339)
    guessedName += ` to team ${teams[firstAction.teamID].name}`;
    // @ts-expect-error TS(2339)
    if (firstAction.randomAgent) {
      guessedName += " and agent randomly";
    }

    // @ts-expect-error TS(2339)
    if (firstAction.agentID === null) {
      guessedName += " and agent unassigned";
    }

    // @ts-expect-error TS(2339)
    if (typeof firstAction.agentID === "string") {
      // @ts-expect-error TS(2339)
      guessedName += ` and agent ${agents[firstAction.agentID].name}`;
    }
  }

  if (guessedName === currentName) {
    return null;
  }

  return guessedName;
};
